/**
 * APT Groups layer renderer.
 * Renders APT groups as icons at their attributed state sponsor locations.
 * Color-coded by sponsor nation.
 */

import { IconLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import { APT_GROUPS } from '@/config/data';

/** Layer IDs handled by this renderer. */
export const APT_LAYER_IDS = new Set(['apt-groups']);

// ---------------------------------------------------------------------------
// Color palette by state sponsor
// ---------------------------------------------------------------------------

type RGB = [number, number, number];

/** Extract sponsor country from the sponsor string (e.g. "Russia (GRU Unit 26165)" -> "Russia"). */
export function getSponsorKey(sponsor: string): string {
  // Strip parenthetical detail
  const base = sponsor.replace(/\s*\(.*\)/, '').trim();
  // Two spellings of the same actor in the dataset
  if (base === 'Palestinian') return 'Palestine';
  return base;
}

/**
 * Keys must match the sponsor strings in config/data/apt-groups.ts —
 * the audit found 57 of 159 groups falling through to gray because of
 * key mismatches ('United States' vs 'USA') and missing buckets.
 */
const SPONSOR_COLORS: Record<string, RGB> = {
  'China':        [239, 68, 68],    // red
  'Russia':       [59, 130, 246],   // blue
  'Iran':         [16, 185, 129],   // emerald
  'North Korea':  [168, 85, 247],   // purple
  'India':        [245, 158, 11],   // amber
  'Pakistan':     [34, 197, 94],    // green
  'Vietnam':      [236, 72, 153],   // pink
  'Turkey':       [6, 182, 212],    // cyan
  'Israel':       [99, 102, 241],   // indigo
  'USA':          [100, 116, 139],  // slate
  'Criminal':     [217, 119, 6],    // burnt amber — non-state, largest bucket
  'South Korea':  [125, 211, 252],  // light blue
  'Lebanon':      [190, 24, 93],    // magenta
  'Belarus':      [161, 98, 7],     // olive
  'UAE':          [200, 160, 80],   // sand
  'Palestine':    [21, 128, 61],    // dark green
};

/** Honest "no attribution" gray for Unknown and anything unmapped. */
const DEFAULT_COLOR: RGB = [148, 163, 184];

function getSponsorColor(sponsor: string): RGB {
  const key = getSponsorKey(sponsor);
  return SPONSOR_COLORS[key] ?? DEFAULT_COLOR;
}

// ---------------------------------------------------------------------------
// Icon generation
// ---------------------------------------------------------------------------

/** SVG data URI cache. */
const aptIconCache = new Map<string, string>();

function createAptIcon(sponsor: string): string {
  const key = `apt-${sponsor}`;
  const cached = aptIconCache.get(key);
  if (cached) return cached;

  const [r, g, b] = getSponsorColor(sponsor);
  // Globe/state icon path
  const path = 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48">
    <circle cx="12" cy="12" r="11" fill="rgba(${r},${g},${b},0.15)" stroke="rgb(${r},${g},${b})" stroke-width="1"/>
    <path d="${path}" fill="rgb(${r},${g},${b})" opacity="0.9"/>
  </svg>`;

  const encoded = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  aptIconCache.set(key, encoded);
  return encoded;
}

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

/** Create deck.gl layers for APT group markers. */
export function createAptLayers(zoom: number): Layer[] {
  if (APT_GROUPS.length === 0) return [];

  const layers: Layer[] = [];
  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  // Add slight offset to groups at the same location so they don't stack
  const offsetGroups = APT_GROUPS.map((g, i) => ({
    ...g,
    offsetLat: g.lat + Math.sin(i * 2.39996) * 0.4 * Math.max(1, 6 - zoom) * 0.2,
    offsetLon: g.lon + Math.cos(i * 2.39996) * 0.4 * Math.max(1, 6 - zoom) * 0.2,
  }));

  // Icon markers
  layers.push(new IconLayer<typeof offsetGroups[0]>({
    id: 'apt-groups-icons',
    data: offsetGroups,
    pickable: true,
    getPosition: (d) => [d.offsetLon, d.offsetLat],
    getIcon: (d) => ({
      url: createAptIcon(d.sponsor),
      width: 48,
      height: 48,
      anchorY: 24,
    }),
    // Closer to WM's 4-8 px footprint so APT markers stop out-shouting the
    // cyber-threat dots they share a toggle with (audit finding)
    getSize: zoom < 5 ? 8 : 14,
    sizeUnits: 'pixels' as const,
    opacity,
    updateTriggers: {
      getPosition: [zoom],
      getSize: [zoom],
    },
  }));

  // Labels at higher zoom
  if (zoom >= 5) {
    layers.push(new TextLayer<typeof offsetGroups[0]>({
      id: 'apt-groups-labels',
      data: offsetGroups,
      getPosition: (d) => [d.offsetLon, d.offsetLat],
      getText: (d) => d.name,
      getSize: 10,
      getColor: (d) => {
        const [r, g, b] = getSponsorColor(d.sponsor);
        return [r, g, b, 220];
      },
      getTextAnchor: 'start' as const,
      getAlignmentBaseline: 'center' as const,
      getPixelOffset: [14, 0],
      fontFamily: 'monospace',
      sizeUnits: 'pixels' as const,
      billboard: true,
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 200],
      updateTriggers: {
        getPosition: [zoom],
      },
    }));
  }

  return layers;
}
